import { ReactNode, memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { organizationTeamPath } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  useOrganizationRolesQuery,
  useRemoveOrganizationMemberMutation,
  useSetOrganizationMemberRoleMutation,
} from '../../../hooks/useOrganizationTeamQueries';
import { UserOrganizationV2 } from '../../../types/dashboardV2';
import { TeamScope } from '../../../types/organizationTeam';
import { teamErrorText } from '../Team/teamErrorText';
import { AttachOrganizationDialog } from './AttachOrganizationDialog';
import { ConfirmChangeDialog } from './components/ConfirmChangeDialog';

interface Props {
  userId: string;
  organization: UserOrganizationV2 | null;
  canJoinOrganization: boolean;
}

/** The staff Team page needs organizations.manage, so others get plain text. */
const OrgTeamLink = ({ orgId, enabled, children }: { orgId: string; enabled: boolean; children: ReactNode }) =>
  enabled ? <Link to={organizationTeamPath(orgId)}>{children}</Link> : <>{children}</>;

/** Side-column panel: which organization the user is in, and (staff) changing it. */
export const UserOrganizationCard = memo(function UserOrganizationCard({
  userId,
  organization,
  canJoinOrganization,
}: Props) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const canManage = has('organizations.manage');
  const member = organization?.kind === 'member' ? organization : null;
  const scope: TeamScope = { kind: 'org', orgId: member?.id ?? '' };

  const roles = useOrganizationRolesQuery(scope, Boolean(member) && canManage);
  const setRole = useSetOrganizationMemberRoleMutation(scope);
  const remove = useRemoveOrganizationMemberMutation(scope);

  const [isAttachOpen, setAttachOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();

  if (!canManage && !has('organizations.view')) return null;

  const roleName = (id: string | null) =>
    roles.data?.find((r) => r.id === id)?.name ?? id ?? '';

  const run = async (fn: () => Promise<unknown>) => {
    setError(undefined);
    setConfirmRemove(false);
    setPendingRoleId(null);
    try {
      await fn();
    } catch (err) {
      setError(teamErrorText(err, t));
    }
  };

  return (
    <section className="ud-panel" aria-labelledby="ud-org-title">
      <h2 id="ud-org-title" className="ud-panel-title">{t('dashboard.userDetails.org.title')}</h2>

      {organization?.kind === 'owner' && (
        <p className="ud-org-line">
          <OrgTeamLink orgId={organization.id} enabled={canManage}>
            {t('dashboard.userDetails.org.ownerOf', { name: organization.name })}
          </OrgTeamLink>
        </p>
      )}

      {member && (
        <>
          <p className="ud-org-line">
            <OrgTeamLink orgId={member.id} enabled={canManage}>{member.name}</OrgTeamLink>
          </p>
          {canManage ? (
            <label className="ud-org-role">
              <span className="ud-panel-hint">{t('dashboard.userDetails.org.role')}</span>
              <select
                className="team-role-select"
                value={member.role?.id ?? ''}
                disabled={setRole.isPending || !roles.data}
                onChange={(e) => setPendingRoleId(e.target.value)}
              >
                {!member.role && <option value="">—</option>}
                {(roles.data ?? (member.role ? [member.role] : [])).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </label>
          ) : (
            member.role && <span className="ud-role-chip is-on">{member.role.name}</span>
          )}
          {canManage && (
            <>
              <button type="button" className="ud-btn ud-btn-block" onClick={() => setConfirmRemove(true)}>
                {t('dashboard.userDetails.org.remove')}
              </button>
              <ConfirmChangeDialog
                open={pendingRoleId !== null}
                message={t('dashboard.userDetails.confirm.orgRoleMessage', {
                  org: member.name,
                  from: member.role?.name ?? t('dashboard.userDetails.confirm.none'),
                  to: roleName(pendingRoleId),
                })}
                isSubmitting={setRole.isPending}
                onCancel={() => setPendingRoleId(null)}
                onConfirm={() => {
                  const roleId = pendingRoleId;
                  if (roleId) void run(() => setRole.mutateAsync({ userId, roleId }));
                }}
              />
              <ConfirmChangeDialog
                open={confirmRemove}
                message={t('dashboard.userDetails.org.removeConfirm', { name: member.name })}
                changes={[
                  {
                    label: t('dashboard.userDetails.org.title'),
                    from: member.name,
                    to: t('dashboard.userDetails.confirm.none'),
                  },
                ]}
                confirmLabel={t('dashboard.userDetails.org.removeConfirmButton')}
                danger
                isSubmitting={remove.isPending}
                onCancel={() => setConfirmRemove(false)}
                onConfirm={() => void run(() => remove.mutateAsync(userId))}
              />
            </>
          )}
        </>
      )}

      {!organization && (
        <>
          <p className="ud-panel-hint">{t('dashboard.userDetails.org.none')}</p>
          {canManage && (
            <>
              <button
                type="button"
                className="ud-btn ud-btn-block"
                disabled={!canJoinOrganization}
                onClick={() => setAttachOpen(true)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">add</span>
                {t('dashboard.userDetails.org.attach')}
              </button>
              {!canJoinOrganization && <p className="ud-panel-hint">{t('dashboard.userDetails.org.cannotJoin')}</p>}
            </>
          )}
        </>
      )}

      {error && <p className="ud-panel-error" role="alert">{error}</p>}

      {isAttachOpen && <AttachOrganizationDialog userId={userId} onClose={() => setAttachOpen(false)} />}
    </section>
  );
});
